// In production (Netlify), use relative URLs so API calls go to Netlify Functions
// In development, use localhost:3001 for local backend
const API_BASE_URL = import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD ? "" : "http://localhost:3001");

export interface Project {
  id: string; // UUID
  title: string;
  description: string;
  client: string;
  provider: string;
  totalAmount: string;
  depositedAmount: string;
  status: "active" | "completed" | "cancelled";
  contractAddress: string;
  blockchainProjectId?: number | null;
  lastDepositTxHash?: string | null;
  lastDepositDate?: string | null;
  milestones: Milestone[];
  createdAt: string;
}

export interface Milestone {
  id: string; // UUID
  amount: string;
  description: string;
  approvedByClient: boolean;
  approvedByProvider: boolean;
  released: boolean;
  disputeStatus?: "none" | "open" | "under_review" | "resolved";
  disputeInitiator?: string | null;
  disputeOpenedAt?: string | null;
}

export interface Dispute {
  id: string; // UUID
  projectId: string;
  milestoneIndex: number;
  blockchainProjectId: number;
  initiatorAddress: string;
  reason: string;
  status: "open" | "under_review" | "resolved";
  clientAmountPercent: number | null;
  providerAmountPercent: number | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  openedAt: string;
  createdAt: string;
  evidenceCount?: number;
}

export interface Evidence {
  id: string;
  disputeId: string;
  submittedBy: string;
  evidenceType: string;
  blobId: string | null;
  evidenceText: string | null;
  submissionNotes: string | null;
  submittedAt: string;
}

export interface DisputeDetail extends Dispute {
  project?: {
    title: string;
    clientAddress: string;
    providerAddress: string;
    arbitratorAddress: string | null;
  };
  milestone?: {
    amount: string;
    description: string;
  };
  evidence?: Evidence[];
}

export interface User {
  id: string; // UUID
  email: string;
  name: string;
  picture: string;
  googleId?: string;
  walletAddress?: string;
  createdAt: string;
}

const apiRequest = async (endpoint: string, options?: RequestInit) => {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const api = {
  // Auth
  googleLogin: async (userData: { email: string; name: string; picture: string; googleId: string }) => {
    return apiRequest("/api/auth/google", {
      method: "POST",
      body: JSON.stringify(userData),
      credentials: "include",
    });
  },

  getCurrentUser: async () => {
    return apiRequest("/api/auth/me", {
      credentials: "include",
    });
  },

  logout: async () => {
    return apiRequest("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    });
  },

  connectWallet: async (walletAddress: string) => {
    return apiRequest("/api/auth/wallet-connect", {
      method: "POST",
      body: JSON.stringify({ walletAddress }),
      credentials: "include",
    });
  },

  // Projects
  getProjects: async (walletAddress?: string) => {
    const url = walletAddress
      ? `/api/projects?walletAddress=${walletAddress}`
      : "/api/projects";
    return apiRequest(url);
  },

  getProject: async (id: string) => {
    return apiRequest(`/api/projects/${id}`);
  },

  createProject: async (projectData: {
    title: string;
    description?: string;
    client: string;
    provider: string;
    totalAmount: string;
    milestones: Array<{ amount: string; description: string }>;
    contractAddress?: string;
    projectId?: number;
  }) => {
    return apiRequest("/api/projects", {
      method: "POST",
      body: JSON.stringify(projectData),
    });
  },

  approveMilestone: async (
    projectId: string,
    milestoneId: number,
    walletAddress: string,
    isClient: boolean
  ) => {
    return apiRequest(`/api/projects/${projectId}/milestones/${milestoneId}/approve`, {
      method: "PUT",
      body: JSON.stringify({ walletAddress, isClient }),
    });
  },

  releaseMilestone: async (projectId: string, milestoneId: number) => {
    const response = await apiRequest(`/api/projects/${projectId}/milestones/${milestoneId}/release`, {
      method: "PUT",
    });
    return response;
  },

  depositFunds: async (projectId: string, amount: string, txHash?: string) => {
    return apiRequest(`/api/projects/${projectId}/deposit`, {
      method: "PUT",
      body: JSON.stringify({ amount, txHash }),
    });
  },

  syncProject: async (projectId: string, blockchainProjectId: number, depositedAmount: string) => {
    return apiRequest(`/api/projects/${projectId}/sync`, {
      method: "POST",
      body: JSON.stringify({ blockchainProjectId, depositedAmount }),
    });
  },

  // Payments
  createPaymentOrder: async (amount: number, currency: string = "USD") => {
    return apiRequest("/api/payments/create-order", {
      method: "POST",
      body: JSON.stringify({ amount, currency }),
      credentials: "include",
    });
  },

  // Demo data
  getDemoProjects: async () => {
    return apiRequest("/api/demo/projects");
  },

  // Transactions
  getTransactions: async (projectId: string) => {
    const response = await apiRequest(`/api/projects/${projectId}/transactions`);
    return response.transactions || [];
  },

  syncTransactions: async (projectId: string, transactions: any[]) => {
    const response = await apiRequest(`/api/projects/${projectId}/transactions/sync`, {
      method: "POST",
      body: JSON.stringify({ transactions }),
    });
    return response.transactions || [];
  },

  syncBlockchain: async (projectId: string) => {
    const response = await apiRequest(`/api/projects/${projectId}/sync-blockchain`, {
      method: "POST",
    });
    return response;
  },

  // Documents
  uploadDocument: async (documentData: {
    projectId: string;
    milestoneIndex?: number;
    fileName: string;
    fileSize: number;
    fileType: string;
    fileData: string; // base64
    comment?: string;
    uploadedBy: string;
  }) => {
    return apiRequest(`/api/projects/${documentData.projectId}/documents`, {
      method: "POST",
      body: JSON.stringify(documentData),
    });
  },

  getDocuments: async (projectId: string, milestoneIndex?: number) => {
    const url = milestoneIndex !== undefined
      ? `/api/projects/${projectId}/documents?milestoneIndex=${milestoneIndex}`
      : `/api/projects/${projectId}/documents`;
    const response = await apiRequest(url);
    return response.documents || [];
  },

  // Disputes
  openDispute: async (projectId: string, milestoneIndex: number, reason: string, txHash: string, initiatorAddress?: string) => {
    return apiRequest(`/api/projects/${projectId}/milestones/${milestoneIndex}/dispute`, {
      method: "POST",
      body: JSON.stringify({ reason, tx_hash: txHash, initiator_address: initiatorAddress }),
    });
  },

  getDisputes: async (projectId: string, status?: string) => {
    const url = status
      ? `/api/projects/${projectId}/disputes?status=${status}`
      : `/api/projects/${projectId}/disputes`;
    const response = await apiRequest(url);
    return response.disputes || [];
  },

  getDisputeDetail: async (projectId: string, milestoneIndex: number) => {
    const response = await apiRequest(`/api/projects/${projectId}/milestones/${milestoneIndex}/dispute`);
    return response.dispute || null;
  },

  setArbitrator: async (projectId: string, arbitratorAddress: string, txHash: string) => {
    return apiRequest(`/api/projects/${projectId}/arbitrator`, {
      method: "POST",
      body: JSON.stringify({ arbitrator_address: arbitratorAddress, tx_hash: txHash }),
    });
  },

  resolveDispute: async (
    projectId: string,
    milestoneIndex: number,
    clientSharePercent: number,
    providerSharePercent: number,
    txHash: string
  ) => {
    return apiRequest(`/api/projects/${projectId}/milestones/${milestoneIndex}/dispute/resolve`, {
      method: "POST",
      body: JSON.stringify({
        client_amount_percent: clientSharePercent,
        provider_amount_percent: providerSharePercent,
        tx_hash: txHash,
      }),
    });
  },

  submitEvidence: async (
    projectId: string,
    milestoneIndex: number,
    evidenceData: {
      evidenceType: 'text' | 'document';
      evidenceText?: string;
      fileName?: string;
      fileSize?: number;
      fileType?: string;
      fileData?: string;
      submissionNotes?: string;
      submittedBy: string;
    }
  ) => {
    return apiRequest(`/api/projects/${projectId}/milestones/${milestoneIndex}/dispute/evidence`, {
      method: "POST",
      body: JSON.stringify(evidenceData),
    });
  },

  getEvidence: async (projectId: string, milestoneIndex: number) => {
    const response = await apiRequest(`/api/projects/${projectId}/milestones/${milestoneIndex}/dispute/evidence`);
    return response.evidence || [];
  },
};

